function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { forwardRef, useRef, useImperativeHandle } from 'react';
import { useIsomorphicLayoutEffect } from '../shared/use-isomorphic-layout-effect';
import { classNames, getExtraAttrs, emit, getSlots } from '../shared/utils';
import { colorClasses } from '../shared/mixins';
import { f7ready, f7 } from '../shared/f7';
import { watchProp } from '../shared/watch-prop';
import { modalStateClasses } from '../shared/modal-state-classes';


var Sheet = /*#__PURE__*/forwardRef(function (props, ref) {
  var _f7Sheet = useRef(null);

  var className = props.className,
      id = props.id,
      style = props.style,
      top = props.top,
      bottom = props.bottom,
      position = props.position,
      push = props.push,
      opened = props.opened,
      animate = props.animate,
      backdrop = props.backdrop,
      backdropEl = props.backdropEl,
      closeByBackdropClick = props.closeByBackdropClick,
      closeByOutsideClick = props.closeByOutsideClick,
      closeOnEscape = props.closeOnEscape,
      swipeToClose = props.swipeToClose,
      swipeToStep = props.swipeToStep,
      swipeHandler = props.swipeHandler,
      containerEl = props.containerEl;
  var extraAttrs = getExtraAttrs(props);
  var elRef = useRef(null);
  var isOpened = useRef(opened);
  var isClosing = useRef(false);

  var onStepProgress = function onStepProgress(instance, progress) {
    emit(props, 'sheetStepProgress', instance, progress);
  };

  var onStepOpen = function onStepOpen(instance) {
    emit(props, 'sheetStepOpen', instance);
  };

  var onStepClose = function onStepClose(instance) {
    emit(props, 'sheetStepClose', instance);
  };

  var onOpen = function onOpen(instance) {
    isOpened.current = true;
    isClosing.current = false;
    emit(props, 'sheetOpen', instance);
  };

  var onOpened = function onOpened(instance) {
    emit(props, 'sheetOpened', instance);
  };

  var onClose = function onClose(instance) {
    isOpened.current = false;
    isClosing.current = true;
    emit(props, 'sheetClose', instance);
  };

  var onClosed = function onClosed(instance) {
    isClosing.current = false;
    emit(props, 'sheetClosed', instance);
  };

  useImperativeHandle(ref, function () {
    return {
      el: elRef.current,
      f7Sheet: function f7Sheet() {
        return _f7Sheet.current;
      }
    };
  });

  var modalEvents = function modalEvents(method) {
    if (!_f7Sheet.current) return;

    _f7Sheet.current[method]('open', onOpen);

    _f7Sheet.current[method]('opened', onOpened);

    _f7Sheet.current[method]('close', onClose);

    _f7Sheet.current[method]('closed', onClosed);

    _f7Sheet.current[method]('stepOpen', onStepOpen);

    _f7Sheet.current[method]('stepClose', onStepClose);

    _f7Sheet.current[method]('stepProgress', onStepProgress);
  };

  var onMount = function onMount() {
    if (!elRef.current) return;
    var sheetParams = {
      el: elRef.current
    };
    if ('animate' in props && typeof animate !== 'undefined') sheetParams.animate = animate;
    if ('backdrop' in props && typeof backdrop !== 'undefined') sheetParams.backdrop = backdrop;
    if ('backdropEl' in props) sheetParams.backdropEl = backdropEl;
    if ('closeByBackdropClick' in props) sheetParams.closeByBackdropClick = closeByBackdropClick;
    if ('closeByOutsideClick' in props) sheetParams.closeByOutsideClick = closeByOutsideClick;
    if ('closeOnEscape' in props) sheetParams.closeOnEscape = closeOnEscape;
    if ('swipeToClose' in props) sheetParams.swipeToClose = swipeToClose;
    if ('swipeToStep' in props) sheetParams.swipeToStep = swipeToStep;
    if ('swipeHandler' in props) sheetParams.swipeHandler = swipeHandler;
    if ('containerEl' in props) sheetParams.containerEl = containerEl;
    f7ready(function () {
      _f7Sheet.current = f7.sheet.create(sheetParams);
      modalEvents('on');

      if (opened) {
        _f7Sheet.current.open(false);
      }
    });
  };

  var onDestroy = function onDestroy() {
    if (_f7Sheet.current) {
      _f7Sheet.current.destroy();
    }

    _f7Sheet.current = null;
  };

  useIsomorphicLayoutEffect(function () {
    modalEvents('on');
    return function () {
      modalEvents('off');
    };
  });
  useIsomorphicLayoutEffect(function () {
    onMount();
    return onDestroy;
  }, []);
  watchProp(opened, function (value) {
    if (!_f7Sheet.current) return;

    if (value) {
      _f7Sheet.current.open();
    } else {
      _f7Sheet.current.close();
    }
  });
  var slots = getSlots(props);
  var fixedList = [];
  var staticList = [];
  var fixedTags = 'navbar toolbar tabbar subnavbar searchbar messagebar fab list-index panel'.split(' ').map(function (tagName) {
    return "f7-" + tagName;
  });
  var slotsDefault = slots.default;

  if (slotsDefault && slotsDefault.length) {
    slotsDefault.forEach(function (child) {
      if (typeof child === 'undefined') return;
      var isFixedTag = false;
      var tag = child.type && (child.type.displayName || child.type.name);

      if (!tag) {
        staticList.push(child);
        return;
      }

      if (fixedTags.indexOf(tag) >= 0) {
        isFixedTag = true;
      }

      if (isFixedTag) fixedList.push(child);else staticList.push(child);
    });
  }

  var innerEl = /*#__PURE__*/React.createElement("div", {
    className: "sheet-modal-inner"
  }, staticList, slots.static);
  var positionComputed = 'bottom';
  if (position) positionComputed = position;else if (top) positionComputed = 'top';else if (bottom) positionComputed = 'bottom';
  var classes = classNames(className, 'sheet-modal', "sheet-modal-" + positionComputed, {
    'sheet-modal-push': push
  }, modalStateClasses({
    isOpened: isOpened,
    isClosing: isClosing
  }), colorClasses(props));
  return /*#__PURE__*/React.createElement("div", _extends({
    id: id,
    style: style,
    className: classes,
    ref: elRef
  }, extraAttrs), fixedList, slots.fixed, innerEl);
});
Sheet.displayName = 'f7-sheet';
export default Sheet;